import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { getUnitFilingProposals, applyUnitFiling } from 'services/fileService'
import SearchableSelect from 'components/SearchableSelect'

const CONF = {
  high: { label: 'High confidence', fg: '#15803d', bg: '#dcfce7' },
  medium: { label: 'Medium confidence', fg: '#d97706', bg: '#fef3c7' },
  low: { label: 'Low confidence', fg: '#c2410c', bg: '#ffedd5' },
  none: { label: 'No match, pick a unit', fg: '#6b7280', bg: '#f1f5f9' },
}

const FileToUnitsWizard = ({ bpId, onClose, onDone }) => {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])           // { file, unitId, confidence, alternatives, checked }
  const [units, setUnits] = useState([])
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [unitsNeedNaming, setUnitsNeedNaming] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getUnitFilingProposals(bpId)
      .then(res => {
        if (cancelled) return
        setUnits(res.data.units || [])
        setUnitsNeedNaming(!!res.data.meta?.unitsNeedNaming)
        setRows((res.data.data || []).map(p => ({
          file: p.file,
          unitId: p.proposedUnit?.id || '',
          confidence: p.confidence,
          alternatives: p.alternatives || [],
          checked: p.confidence === 'high' && !!p.proposedUnit,
        })))
      })
      .catch(() => !cancelled && toast.error('Could not load documents to file.'))
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [bpId])

  const setRow = (fileId, patch) =>
    setRows(prev => prev.map(r => r.file.id === fileId ? { ...r, ...patch } : r))

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? rows.filter(r => (r.file.displayName || '').toLowerCase().includes(q)) : rows
  }, [rows, query])

  const selectedPairs = rows
    .filter(r => r.checked && r.unitId)
    .map(r => ({ fileId: r.file.id, buildingUnitPassportId: r.unitId }))

  const selectAllHigh = () =>
    setRows(prev => prev.map(r => r.confidence === 'high' && r.unitId ? { ...r, checked: true } : r))

  const handleApply = () => {
    if (!selectedPairs.length) return
    setSaving(true)
    applyUnitFiling(bpId, selectedPairs)
      .then(res => {
        toast.success(`Filed ${res.data?.data?.shared ?? selectedPairs.length} document(s) to their units.`)
        setRows(prev => prev.filter(r => !(r.checked && r.unitId)))
        onDone?.()
      })
      .catch(error => toast.error(error?.response?.data?.message || 'Could not file the documents. Please try again.'))
      .finally(() => setSaving(false))
  }

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={e => e.stopPropagation()}>
        <Header>
          <div>
            <Title>File documents to units</Title>
            <Sub>We&rsquo;ve suggested the unit for each document. Tick the ones to file, adjust any that are wrong, then share. Nothing is shared until you click.</Sub>
          </div>
          <IconBtn onClick={onClose} aria-label='Close'><CloseRoundedIcon /></IconBtn>
        </Header>

        {!loading && unitsNeedNaming && (
          <Notice>
            <NoticeTitle>Your units don&rsquo;t have names yet</NoticeTitle>
            <NoticeText>
              We can&rsquo;t suggest which unit each document belongs to until your units are named. Give each unit a name or plot number in the Units tab, then reopen this and we&rsquo;ll match your documents automatically. You can still choose a unit for each document below.
            </NoticeText>
          </Notice>
        )}

        {loading ? (
          <Skeletons>{Array.from({ length: 6 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
        ) : rows.length === 0 ? (
          <Empty>No documents waiting to be filed to units.</Empty>
        ) : (
          <>
            <Toolbar>
              <Search value={query} onChange={e => setQuery(e.target.value)} placeholder='Search documents' />
              <LinkBtn type='button' onClick={selectAllHigh}>Select all high‑confidence</LinkBtn>
              <Count>{selectedPairs.length} selected</Count>
            </Toolbar>
            <Table data-tour-id='fileToUnits.table'>
              <THead>
                <th style={{ width: 34 }} />
                <th>Document</th>
                <th>Unit</th>
                <th>AI confidence</th>
              </THead>
              {visible.map(r => {
                const c = CONF[r.confidence] || CONF.none
                return (
                  <Row key={r.file.id}>
                    <td>
                      <input
                        type='checkbox'
                        checked={r.checked}
                        disabled={!r.unitId}
                        onChange={e => setRow(r.file.id, { checked: e.target.checked })}
                      />
                    </td>
                    <td>
                      <FileName>{r.file.displayName}</FileName>
                      {r.file.fileType && <TypeChip>{r.file.fileType}</TypeChip>}
                    </td>
                    <td>
                      <SearchableSelect
                        value={r.unitId}
                        onChange={v => setRow(r.file.id, { unitId: v, checked: !!v })}
                        options={units.map(u => ({ value: u.id, label: u.name }))}
                        placeholder='— choose a unit —'
                        searchPlaceholder='Search units…'
                        ariaLabel='Choose a unit'
                      />
                    </td>
                    <td><Badge $fg={c.fg} $bg={c.bg}>{c.label}</Badge></td>
                  </Row>
                )
              })}
            </Table>
          </>
        )}

        <Footer>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn data-tour-id='fileToUnits.apply' disabled={saving || !selectedPairs.length} onClick={handleApply}>
            {saving ? 'Filing…' : `Share ${selectedPairs.length} to units`}
          </PrimaryBtn>
        </Footer>
      </Card>
    </Backdrop>
  )
}

export default FileToUnitsWizard

const Backdrop = styled.div`position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 1500; padding: 16px;`
const Card = styled.div`background: #fff; border-radius: 12px; width: 760px; max-width: 100%; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 10px 40px rgba(0,0,0,0.2);`
const Header = styled.div`display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding: 18px 20px; border-bottom: 1px solid #eef0f3;`
const Title = styled.h3`margin: 0; font-size: 16px; font-weight: 800; color: ${({ theme }) => theme.colors.haiti};`
const Sub = styled.div`margin-top: 4px; font-size: 12.5px; color: #667085; max-width: 560px;`
const IconBtn = styled.button`border: none; background: transparent; cursor: pointer; color: #98a2b3; &:hover { color: ${({ theme }) => theme.colors.haiti}; }`
const Toolbar = styled.div`display: flex; align-items: center; gap: 12px; padding: 12px 20px;`
const Search = styled.input`flex: 1; padding: 7px 10px; border: 1px solid #e4e4e7; border-radius: 8px; font: inherit; font-size: 13px;`
const LinkBtn = styled.button`border: none; background: transparent; color: ${({ theme }) => theme.colors.secondary}; font-weight: 700; font-size: 12.5px; cursor: pointer;`
const Count = styled.span`font-size: 12.5px; color: #667085;`
const Table = styled.div`overflow: auto; padding: 0 20px;`
const THead = styled.div`display: grid; grid-template-columns: 34px 1fr 200px 160px; font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #98a2b3; padding: 6px 0; position: sticky; top: 0; background: #fff; & th { text-align: left; font-weight: 800; }`
const Row = styled.div`display: grid; grid-template-columns: 34px 1fr 200px 160px; align-items: center; padding: 10px 0; border-top: 1px solid #f2f4f7; & td { min-width: 0; }`
const FileName = styled.div`font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.haiti}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const TypeChip = styled.span`display: inline-block; margin-top: 2px; font-size: 10.5px; font-weight: 700; color: #334155; background: #f1f5f9; border-radius: 999px; padding: 1px 8px;`
const Badge = styled.span`display: inline-flex; align-items: center; font-size: 11px; font-weight: 800; padding: 2px 9px; border-radius: 999px; color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg};`
const Footer = styled.div`display: flex; justify-content: flex-end; gap: 10px; padding: 16px 20px; border-top: 1px solid #eef0f3;`
const GhostBtn = styled.button`font: inherit; padding: 9px 16px; background: transparent; border: 1px solid #ececec; border-radius: 999px; font-weight: 700; font-size: 12.5px; cursor: pointer;`
const PrimaryBtn = styled.button`font: inherit; padding: 9px 16px; background: ${({ theme }) => theme.colors.secondary}; color: #fff; border: none; border-radius: 999px; font-weight: 800; font-size: 12.5px; cursor: pointer; &:disabled { opacity: .5; cursor: not-allowed; }`
const Empty = styled.div`padding: 40px; text-align: center; color: #667085; font-size: 13px;`
const Notice = styled.div`margin: 14px 20px 0; padding: 12px 14px; background: #fff7ed; border: 1px solid #ffedd5; border-radius: 10px;`
const NoticeTitle = styled.div`font-size: 13px; font-weight: 800; color: #7c2d12;`
const NoticeText = styled.div`margin-top: 4px; font-size: 12.5px; line-height: 1.5; color: #7c2d12;`
const Skeletons = styled.div`padding: 16px 20px; display: flex; flex-direction: column; gap: 10px;`
const SkelRow = styled.div`height: 40px; border-radius: 8px; background: linear-gradient(90deg,#f2f4f7,#f8fafc,#f2f4f7); background-size: 200% 100%; animation: sh 1.2s ease-in-out infinite; @keyframes sh { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`
